import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { matchIsValidTel } from "mui-tel-input";
import { ChangeEvent, useEffect, useState } from "react";
import * as Yup from "yup";
import {
  CreateUserRequestDTO,
  useCreateUserMutation,
  useGetMeQuery,
  // ⬇️ NEW
  useCheckExistsMutation,
  useResetPasswordMutation,
} from "../redux/usersApi";
import { Role } from "../typings/enums";
import CreateUserSuccessDialog from "./CreateUserSuccessDialog";
import UserDetailsForm from "./UserDetailsForm";
import { useNavigate } from "react-router-dom";
import {AdminForm} from "./AdminForm";

interface CreateUserDialogProps {
  handleClose: () => void;
  isLoading: boolean;
  open: boolean;
  orgId: string;
}

export default function CreateUserDialog(props: CreateUserDialogProps) {
  const navigate = useNavigate();
  const { currentData: userData, isError } = useGetMeQuery();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createUser, { isLoading, data, isSuccess, status }] =
    useCreateUserMutation();

  // ⬇️ NEW hooks for duplicate check + password reset
  const [checkExists] = useCheckExistsMutation();
  const [resetPassword] = useResetPasswordMutation();

  const [createUserSuccessDialogOpen, setCreateUserSuccessDialogOpen] =
    useState(false);

  // ⬇️ NEW: holds a one-time password when we recover via reset
  const [manualOneTimePassword, setManualOneTimePassword] = useState<string | null>(null);

  useEffect(() => {
    if (isError) {
      navigate("/");
    }
    if (isSuccess) {
      // clear any manual password if the normal create succeeded
      setManualOneTimePassword(null);
      setCreateUserSuccessDialogOpen(true);
    }
  }, [data, data?.oneTimePassword, isError, isSuccess, navigate, status]);

  const orgs = new Set(Object.values(userData?.postalCodes || {}));
  const uniqueOrgArray = Array.from(orgs) as string[];

  const handleCloseDialog = () => {
    formik.resetForm();
    props.handleClose();
  };

  const handleSubmit = async () => {
    const userIsComplete = (
      _user: Partial<CreateUserRequestDTO["user"]>
    ): _user is CreateUserRequestDTO["user"] => {
      return formik.isValid;
    };

    if (!userIsComplete(formik.values)) {
      return;
    }

    try {
      // ⬇️ NEW: pre-flight duplicate check
      const existsRes = await checkExists({ email: formik.values.email as string }).unwrap();
      if (existsRes.exists) {
        setErrorMessage("Es gibt bereits einen Benutzer mit dieser E-Mail-Adresse.");
        setManualOneTimePassword(null);
        setCreateUserSuccessDialogOpen(true); // keep current behavior: show dialog on errors too
        return;
      }

      await createUser({
        user: { ...formik.values, org: props.orgId, validUntil: null },
        orgId: props.orgId,
      }).unwrap();

      setErrorMessage(null);
      setManualOneTimePassword(null);
      setCreateUserSuccessDialogOpen(true);
    } catch (error: unknown) {
      // parse backend error message
      let message = "Unbekannter Fehler";
      if (typeof error === "object" && error !== null && "data" in error) {
        const errData = error as { data?: { error?: string; message?: string } };
        message = errData.data?.error ?? errData.data?.message ?? message;
      }

      // ⬇️ NEW: recovery attempt — was the user created anyway?
      try {
        const existsResAfter = await checkExists({ email: formik.values.email as string }).unwrap();
        if (existsResAfter.exists && existsResAfter.id) {
          // rotate password on the found user and show it
          const resetRes = await resetPassword({ id: existsResAfter.id }).unwrap();
          setErrorMessage(null);
          setManualOneTimePassword(resetRes.oneTimePassword);
          setCreateUserSuccessDialogOpen(true);
          return;
        }
      } catch {
        // swallow and fall through to error UI
      }

      // Could not recover – show original error
      setErrorMessage(message);
      setManualOneTimePassword(null);
      setCreateUserSuccessDialogOpen(true);
    }
  };

  const UserCreateSchema = Yup.object().shape({
    title: Yup.string().required("Bitte wählen Sie einen Titel aus"),
    firstname: Yup.string().required("Bitte geben Sie einen Vornamen ein"),
    lastname: Yup.string().required("Bitte geben Sie einen Nachnamen ein"),
    email: Yup.string()
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        "Bitte geben Sie eine gültige E-Mail ein"
      )
      .required("Bitte geben Sie eine E-Mail ein"),
    phone: Yup.string()
      .test(
        "isValidTel",
        () => "Bitte geben Sie eine gültige Telefonnummer ein",
        (value) => !!value && matchIsValidTel(value, "CH")
      )
      .required("Bitte geben Sie eine Telefonnummer ein"),
    dateOfBirth: Yup.string()
      .typeError("Bitte geben Sie ein gültiges Datum ein")
      .required("Bitte geben Sie ein Geburtsdatum ein"),
    address: Yup.string().required("Bitte geben Sie eine Adresse ein"),
    rokkaHash: Yup.string().optional(),
    postalCode: Yup.number().required("Bitte geben Sie eine Postleitzahl ein"),
    comment: Yup.string().optional(),
    image: Yup.string().required("Bitte laden Sie ein Bild hoch"),
  });

  const formik: FormikProps<Partial<CreateUserRequestDTO["user"]>> = useFormik({
    initialValues: {},
    isInitialValid: false,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: UserCreateSchema,
    onSubmit: (_values) => void 0,
  });

  const handleFieldChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    formik.setFieldValue(e.target.name, e.target.value, false);
    setTimeout(() => formik.setFieldTouched(e.target.name, true, true), 0);
  };

  return (
    <div>
      <Dialog open={props.open} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>
          <Grid container className="flex justify-between">
            <Grid item>Antrag erfassen</Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <form>
            <Grid container rowSpacing={3} columnSpacing={2}>
              <UserDetailsForm
                isCardIssuer={userData?.role === Role.CardIssuer}
                formik={formik}
                userData={userData}
                postalCodes={userData?.postalCodes ?? {}}
                handleFieldChange={handleFieldChange}
              />

              <AdminForm
                  isSuperAdmin={userData?.role === Role.SuperAdmin}
                  orgs={uniqueOrgArray}
                  formik={formik}
                  handleFieldChange={handleFieldChange}
              />
            </Grid>
          </form>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <LoadingButton
            variant="contained"
            loading={isLoading}
            disabled={!formik.isValid}
            onClick={handleSubmit}
          >
            Speichern
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <CreateUserSuccessDialog
          open={createUserSuccessDialogOpen}
          oneTimePassword={data?.oneTimePassword ?? manualOneTimePassword ?? null}
          errorMessage={errorMessage}
          handleClose={() => {
            handleCloseDialog();
            setCreateUserSuccessDialogOpen(false);
            setErrorMessage(null);
          }}
      />
    </div>
  );
}
